import type { Side } from '../game/world';

export interface AppUi {
  readonly canvasHost: HTMLElement;
  readonly status: HTMLElement;
  readonly createButton: HTMLButtonElement;
  readonly joinForm: HTMLFormElement;
  readonly roomInput: HTMLInputElement;
  readonly copyInviteButton: HTMLButtonElement;
  readonly shareInviteButton: HTMLButtonElement;
  readonly moveLeftButton: HTMLButtonElement;
  readonly moveRightButton: HTMLButtonElement;
  readonly startButton: HTMLButtonElement;
  readonly restartButton: HTMLButtonElement;
  readonly deleteRoomButton: HTMLButtonElement;
  setBusy(busy: boolean): void;
  setScore(you: number, rival: number): void;
  setStartVisible(visible: boolean): void;
  setResult(winner: Side | null, yourSide: Side | null): void;
}

export function createAppUi(root: HTMLElement): AppUi {
  root.innerHTML = `
    <section class="hud" aria-label="Game controls">
      <div class="brand">
        <h1>BLIND PONG</h1>
        <p id="status" role="status">Create room or join one</p>
        <p>Move: <kbd>A</kbd>/<kbd>D</kbd>, <kbd>←</kbd>/<kbd>→</kbd>, or touch controls</p>
      </div>
      <div class="score" aria-live="polite"><span>YOU <b id="your-score">0</b></span><i>:</i><span><b id="rival-score">0</b> RIVAL</span></div>
      <div class="room-controls">
        <button id="create-room" type="button">Create room</button>
        <button id="copy-invite" type="button" hidden>Copy invite link</button>
        <button id="share-invite" type="button" hidden>Share invite</button>
        <button id="start-match" type="button" hidden>Start</button>
        <form id="join-room">
          <label for="room-id">Room ID</label>
          <input id="room-id" name="room" required maxlength="64" autocomplete="off" />
          <button type="submit">Join</button>
        </form>
      </div>
    </section>
    <section id="result" class="result" aria-live="assertive" hidden>
      <h2 id="result-title"></h2>
      <div class="result-actions">
        <button id="restart-match" type="button">Restart</button>
        <button id="delete-room" type="button">Delete room</button>
      </div>
    </section>
    <div class="touch-controls" aria-label="Touch movement controls">
      <button id="move-left" type="button" aria-label="Move left">←</button>
      <button id="move-right" type="button" aria-label="Move right">→</button>
    </div>
  `;

  const canvasHost = document.createElement('div');
  canvasHost.className = 'canvas-host';
  canvasHost.setAttribute('aria-label', 'Your half of Blind Pong court');
  root.append(canvasHost);
  const status = requireElement<HTMLElement>(root, '#status');
  const createButton = requireElement<HTMLButtonElement>(root, '#create-room');
  const joinForm = requireElement<HTMLFormElement>(root, '#join-room');
  const roomInput = requireElement<HTMLInputElement>(root, '#room-id');
  const copyInviteButton = requireElement<HTMLButtonElement>(root, '#copy-invite');
  const shareInviteButton = requireElement<HTMLButtonElement>(root, '#share-invite');
  const startButton = requireElement<HTMLButtonElement>(root, '#start-match');
  const moveLeftButton = requireElement<HTMLButtonElement>(root, '#move-left');
  const moveRightButton = requireElement<HTMLButtonElement>(root, '#move-right');
  const yourScore = requireElement<HTMLElement>(root, '#your-score');
  const rivalScore = requireElement<HTMLElement>(root, '#rival-score');
  const result = requireElement<HTMLElement>(root, '#result');
  const resultTitle = requireElement<HTMLElement>(root, '#result-title');
  const restartButton = requireElement<HTMLButtonElement>(root, '#restart-match');
  const deleteRoomButton = requireElement<HTMLButtonElement>(root, '#delete-room');
  return {
    canvasHost, status, createButton, joinForm, roomInput, copyInviteButton, shareInviteButton, moveLeftButton, moveRightButton, startButton, restartButton, deleteRoomButton,
    setBusy(busy) {
      createButton.disabled = busy;
      for (const element of joinForm.elements) if (element instanceof HTMLInputElement || element instanceof HTMLButtonElement) element.disabled = busy;
    },
    setScore(you, rival) { yourScore.textContent = String(you); rivalScore.textContent = String(rival); },
    setStartVisible(visible) { startButton.hidden = !visible; },
    setResult(winner, yourSide) {
      result.hidden = !winner;
      resultTitle.textContent = winner ? (winner === yourSide ? 'YOU WIN' : 'RIVAL WINS') : '';
    },
  };
}

function requireElement<T extends Element>(root: ParentNode, selector: string): T {
  const element = root.querySelector<T>(selector);
  if (!element) throw new Error(`Missing ${selector}`);
  return element;
}
